import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { UpdatePriorityAPI } from '../../projects/api/ProjectService';
import { AddZoneAPI, DeleteZoneAPI, DuplicateZoneAPI, UpdateZoneAPI } from '../api/LocationService';
import {
  addEmptyArea,
  createArea,
  deleteArea,
  removeEmptyArea,
  selectAreaEntities,
} from './areasSlice';

import { normalize, schema } from 'normalizr';
import {
  selectAllLocationsByProjectId,
  selectLocationById,
  setLocationZoneOrder,
} from './locationsSlice';

/** Create Zone
 * @param {*} requestParameters
 * @param {String} requestParameters.name
 * @param {Number} requestParameters.location_id
 */
export const createZone = createAsyncThunk(
  'zones/createZone',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await AddZoneAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/** Update Zone
 * @param {*} requestParameters
 * @param {Number} requestParameters.id
 * @param {Name} requestParameters.name
 * @param {Number} requestParameters.locationId
 */
export const updateZone = createAsyncThunk(
  'zones/updateZone',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await UpdateZoneAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const areaIds = apiResponse.response?.data?.areas?.map(({ id }) => id);
      return { ...apiResponse.response.data, areas: areaIds };
    }
  }
);

/** Delete Zone
 * @param {*} requestParameters
 * @param {Number} requestParameters.id
 */
export const deleteZone = createAsyncThunk(
  'zones/deleteZone',
  async (requestParameters, { rejectWithValue, getState }) => {
    const apiResponse = await DeleteZoneAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const zoneState = getState().zones;
      const zone = requestParameters.id;
      const areas = zoneState.entities[zone]?.areas ? zoneState.entities[zone].areas : [];
      const location = zoneState.entities[zone].location_id;
      return { location, zone, areas };
    }
  }
);

/** Reorder Zones
 * @param {*} requestParameters
 * @param {Number} requestParameters.sourceIndex
 * @param {Number} requestParameters.destinationIndex
 * @param {Number} requestParameters.parentId
 */
export const reorderZones = createAsyncThunk(
  'zones/reorderZones',
  async (requestParameters, { rejectWithValue, getState, dispatch }) => {
    const parentId = requestParameters.parentId;
    const zoneIds = [...getState().locations.entities[parentId].zones];
    let destinationIndex = requestParameters.destinationIndex;
    let sourceIndex = requestParameters.sourceIndex;
    const id = zoneIds.splice(sourceIndex, 1);
    zoneIds.splice(destinationIndex, 0, ...id);
    dispatch(setLocationZoneOrder({ locationId: parentId, zones: zoneIds }));
    const apiResponse = await UpdatePriorityAPI({
      orderingType: 'zone',
      zoneIds,
      parentId: parentId,
    });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/** Duplicate Zone
 * @param {*} requestParameters
 * @param {Number} requestParameters.id
 * @param {Number} requestParameters.locationId
 */
export const duplicateZone = createAsyncThunk(
  'zones/duplicateZone',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await DuplicateZoneAPI(requestParameters.id);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const areaSchema = new schema.Entity('areas');
      const zoneSchema = new schema.Entity('zones', {
        areas: [areaSchema],
      });

      const formattedData = new schema.Array(zoneSchema);

      const formattedResponse = normalize([apiResponse.response.data], formattedData);
      return formattedResponse;
    }
  }
);

const zonesAdapter = createEntityAdapter();

const initialState = zonesAdapter.getInitialState({
  error: null,
});

const zonesSlice = createSlice({
  name: 'zones',
  initialState,
  reducers: {
    clearZones: zonesAdapter.removeAll,
    addEmptyZone: (state, action) => {
      zonesAdapter.addOne(state, {
        id: action.payload.id,
        name: 'New Zone',
        location_id: action.payload.locationId,
      });
    },
    removeEmptyZone: (state, action) => {
      zonesAdapter.removeOne(state, action.payload.id);
    },
    setZoneAreaOrder: (state, action) => {
      state.entities[action.payload.zoneId].areas = action.payload.areas;
    },
    updateZoneSelection: (state, action) => {
      zonesAdapter.upsertMany(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    //Read
    builder.addCase('locations/fetchLocations/fulfilled', (state, action) => {
      if (action.payload.entities?.zones) {
        zonesAdapter.setAll(state, action.payload.entities.zones);
      }
    });
    //Create
    builder.addCase(createZone.fulfilled, (state, action) => {
      zonesAdapter.removeOne(state, action.meta.arg.id);
      zonesAdapter.addOne(state, { ...action.payload, areas: [] });
    });
    builder.addCase(createZone.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Update
    builder.addCase(updateZone.fulfilled, (state, action) => {
      zonesAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(updateZone.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Delete
    builder.addCase(deleteZone.fulfilled, (state, action) => {
      zonesAdapter.removeOne(state, action.payload.zone);
    });
    builder.addCase(deleteZone.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Reorder
    builder.addCase(reorderZones.fulfilled, (state, action) => {
      zonesAdapter.upsertMany(state, action.payload);
    });

    //Location Deleted
    builder.addCase('locations/deleteLocation/fulfilled', (state, action) => {
      zonesAdapter.removeMany(state, action.payload.zones);
    });

    //Area deleted
    builder.addCase(deleteArea.fulfilled, (state, action) => {
      state.entities[action.payload.zone].areas = state.entities[action.payload.zone].areas.filter(
        (areaId) => areaId !== action.payload.area
      );
    });
    builder.addCase(removeEmptyArea, (state, action) => {
      state.entities[action.payload.zoneId].areas = state.entities[
        action.payload.zoneId
      ].areas.filter((areaId) => areaId !== action.payload.id);
    });

    //Empty Area Added
    builder.addCase(addEmptyArea, (state, action) => {
      state.entities[action.payload.zoneId].areas.push(action.payload.id);
    });

    //Area Created
    builder.addCase(createArea.fulfilled, (state, action) => {
      state.entities[action.payload.zone_id].areas = state.entities[
        action.payload.zone_id
      ].areas.filter((areaId) => areaId !== action.meta.arg.id);
      state.entities[action.payload.zone_id].areas.splice(
        action.payload.priority - 1,
        0,
        action.payload.id
      );
    });

    //Duplicate Location
    builder.addCase('locations/duplicateLocations/fulfilled', (state, action) => {
      if (action.payload?.entities?.zones) {
        zonesAdapter.upsertMany(state, action.payload.entities.zones);
      }
    });

    //Duplicate Zone
    builder.addCase(duplicateZone.fulfilled, (state, action) => {
      if (action.payload?.entities?.zones) {
        zonesAdapter.upsertMany(state, action.payload.entities.zones);
      }
    });
  },
});

export default zonesSlice.reducer;

export const { clearZones, addEmptyZone, setZoneAreaOrder, removeEmptyZone, updateZoneSelection } =
  zonesSlice.actions;

export const selectZoneInGroupByLocationId = createSelector(
  [(state, id) => selectLocationById(state, id), (state) => selectZoneEntities(state)],
  (location, zones) => {
    const zoneArray = location?.zones?.map((id) => zones[id]);
    const data = [{ id: location?.id, name: location?.name, children: zoneArray }];
    return data;
  }
);

export const selectAllZoneInGroup = createSelector(
  [
    (state, projectId) => selectAllLocationsByProjectId(state, projectId),
    (state) => selectZoneEntities(state),
  ],
  (locations, zones) => {
    const data = locations.map((location) => {
      const zoneArray = location?.zones?.map((id) => zones[id]);
      return { name: location.name, id: location.id, children: zoneArray };
    });

    return data;
  }
);

export const selectAreasByZoneId = createSelector(
  [(state, zoneId) => selectZoneById(state, zoneId), (state) => selectAreaEntities(state)],
  (zone, areas) => {
    return zone?.areas?.map((id) => areas[id]);
  }
);

/** Selectors **/
export const {
  selectById: selectZoneById,
  selectIds: selectZoneIds,
  selectEntities: selectZoneEntities,
  selectAll: selectAllZones,
  selectTotal: selectTotalZones,
} = zonesAdapter.getSelectors((state) => state.zones);
